import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, Button, Table, TableHead,
  TableRow, TableCell, TableBody, Chip, Stack, Skeleton, Alert, Divider, TableContainer,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RTooltip } from 'recharts';
import * as XLSX from 'xlsx';
import { db } from '../../firebase';
import { CLP, FINANCE_COLORS, fmtFecha, hoyISO, labelMedio } from '../../utils/finance';
import { useBranch } from '../../context/BranchContext';

const { PRIMARY } = FINANCE_COLORS;
const PIE_COLORS = ['#1A3A5C', '#2CB8A0', '#F59E0B', '#6366F1', '#DC2626', '#10B981', '#8B5CF6'];

const primerDiaMes = () => {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export default function LibroVentas() {
  const { activeBranchId, isAdmin, filterByBranch } = useBranch();
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoyISO());
  const [branchId, setBranchId] = useState(activeBranchId || '');
  const [tipoDoc, setTipoDoc] = useState('');
  const [estado, setEstado] = useState('');
  const [branches, setBranches] = useState([]);
  const [tiposPrestacion, setTiposPrestacion] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [buscado, setBuscado] = useState(false);

  useEffect(() => {
    getDocs(collection(db, 'branches')).then((snap) =>
      setBranches(snap.docs.map((d) => ({ id: d.id, ...d.data() })))).catch(() => {});
    getDocs(collection(db, 'tiposPrestacion')).then((snap) =>
      setTiposPrestacion(snap.docs.map((d) => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);

  const branchesMap = useMemo(() => {
    const map = {};
    branches.forEach((b) => { map[b.id] = b; });
    return map;
  }, [branches]);

  // tipoPrestacion (lowercase) -> nombre de la meta a la que tributa
  const metaPorTipoPrestacion = useMemo(() => {
    const map = {};
    tiposPrestacion.forEach((tp) => {
      const tipo = (tp.name || '').trim().toLowerCase();
      const meta = (tp.goalName || tp.meta || '').trim();
      if (tipo && meta) map[tipo] = meta;
    });
    return map;
  }, [tiposPrestacion]);

  const metaDe = (tipoPrestacion) =>
    metaPorTipoPrestacion[(tipoPrestacion || '').trim().toLowerCase()] || '(sin meta)';

  const aplicar = useCallback(async () => {
    setLoading(true); setError(''); setBuscado(true);
    try {
      const from = new Date(desde + 'T00:00:00');
      const to = new Date(hasta + 'T23:59:59');
      const snap = await getDocs(query(
        collection(db, 'Sales'),
        where('createdAt', '>=', Timestamp.fromDate(from)),
        where('createdAt', '<=', Timestamp.fromDate(to)),
        orderBy('createdAt', 'desc'),
      ));
      let list = filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
      if (branchId && branchId !== 'all') list = list.filter((v) => v.branchId === branchId);
      if (tipoDoc) list = list.filter((v) => (v.tipoDocumento || 'boleta') === tipoDoc);
      if (estado) list = list.filter((v) => (v.estado || 'emitida') === estado);
      setVentas(list);
    } catch (e) {
      setError('Error al consultar: ' + e.message + (e.code === 'failed-precondition' ? ' (puede requerir un índice en Firestore)' : ''));
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, branchId, tipoDoc, estado]);

  useEffect(() => { aplicar(); }, [aplicar]);

  const totales = useMemo(() => {
    const t = { exento: 0, neto: 0, iva: 0, total: 0, boletas: 0, facturas: 0, anuladas: 0 };
    ventas.forEach((v) => {
      const anulada = (v.estado || 'emitida') === 'anulada';
      if (anulada) { t.anuladas += 1; return; }
      t.exento += v.montoExento || 0;
      t.neto += v.subtotalNeto || v.subtotal || 0;
      t.iva += v.montoIVA || v.iva || 0;
      t.total += v.totalFinal || v.total || 0;
      if ((v.tipoDocumento || 'boleta') === 'factura') t.facturas += 1; else t.boletas += 1;
    });
    return t;
  }, [ventas]);

  const porUnidad = useMemo(() => {
    const map = {};
    ventas.filter((v) => (v.estado || 'emitida') !== 'anulada').forEach((v) => {
      (v.items || []).forEach((it) => {
        const key = it.unidadNegocio || it.tipoPrestacion || 'Otros';
        map[key] = (map[key] || 0) + (it.total || 0);
      });
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [ventas]);

  const exportarCSV = () => {
    const headers = ['Sucursal', 'Fecha', 'Tipo', 'Folio', 'RUT', 'Razón Social', 'Medio de Pago', 'Cód. Autorización', 'Últ. 4 Dígitos', 'Neto', 'IVA', 'Total', 'Estado'];
    const rows = ventas.map((v) => [
      (branchesMap[v.branchId]?.name || '').replace(/;/g, ' '),
      fmtFecha(v.createdAt),
      v.tipoDocumento || 'boleta',
      v.folioDocumento || v.boletaNumber || '',
      v.rutEmpresa || v.ownerRut || '',
      (v.razonSocial || v.ownerName || '').replace(/;/g, ' '),
      labelMedio(v.pagos?.[0]?.medio || v.paymentMethod),
      v.pagos?.[0]?.voucherNumber || '',
      v.pagos?.[0]?.last4Digits || '',
      v.subtotalNeto || v.subtotal || 0,
      v.montoIVA || v.iva || 0,
      v.totalFinal || v.total || 0,
      v.estado || 'emitida',
    ]);
    const csv = '\ufeff' + [headers, ...rows].map((r) => r.join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `libro_ventas_${desde.replace(/-/g, '')}_${hasta.replace(/-/g, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Convierte Timestamp/Date/string a texto legible para Excel
  const toExcelDate = (v) => {
    if (!v) return '';
    if (typeof v?.toDate === 'function') return v.toDate().toISOString().slice(0, 19).replace('T', ' ');
    if (v instanceof Date) return v.toISOString().slice(0, 19).replace('T', ' ');
    return String(v);
  };

  // Extrae año / mes / periodo de un Timestamp o Date
  const toPeriodo = (v) => {
    let d = null;
    if (typeof v?.toDate === 'function') d = v.toDate();
    else if (v instanceof Date) d = v;
    else if (v) d = new Date(v);
    if (!d || Number.isNaN(d.getTime())) return { anio: '', mes: '', periodo: '', nombreMes: '' };
    const mes = d.getMonth() + 1;
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return {
      anio: d.getFullYear(),
      mes,
      periodo: `${d.getFullYear()}-${String(mes).padStart(2, '0')}`,
      nombreMes: meses[mes - 1],
    };
  };

  // Exporta a Excel con 4 hojas: "Ventas" (una fila por venta),
  // "Detalle Prestaciones" (una fila por ítem), "Resumen x Prestación" y "Resumen x Meta".
  const exportarExcel = () => {
    const ventaFields = (v) => {
      const per = toPeriodo(v.createdAt);
      return {
        anio: per.anio,
        mes: per.mes,
        nombreMes: per.nombreMes,
        periodo: per.periodo,
        sucursal: branchesMap[v.branchId]?.name || '',
        createdAt: toExcelDate(v.createdAt),
        paidAt: toExcelDate(v.paidAt),
        estado: v.estado || 'emitida',
        tipoDocumento: v.tipoDocumento || 'boleta',
        folioDocumento: v.folioDocumento || '',
        boletaNumber: v.boletaNumber || '',
        folioSII: v.folioSII || '',
        cajaNombre: v.cajaNombre || '',
        turnoId: v.turnoId || '',
        convenioName: v.convenioName || '',
        patientName: v.patientName || '',
        ownerName: v.ownerName || '',
        ownerRut: v.ownerRut || '',
        razonSocial: v.razonSocial || '',
        rutEmpresa: v.rutEmpresa || '',
        presupuestoNumber: v.presupuestoNumber || '',
        orderType: v.orderType || '',
        orderNumber: v.orderNumber || '',
        venta_subtotal: Number(v.subtotal) || 0,
        venta_subtotalNeto: Number(v.subtotalNeto) || 0,
        venta_montoExento: Number(v.montoExento) || 0,
        venta_iva: Number(v.iva) || 0,
        venta_montoIVA: Number(v.montoIVA) || 0,
        venta_descuentoTotal: Number(v.descuentoTotal) || 0,
        venta_total: Number(v.total) || 0,
        venta_totalFinal: Number(v.totalFinal) || 0,
        saldoPendiente: Number(v.saldoPendiente) || 0,
        vuelto: Number(v.vuelto) || 0,
        paymentMethod: v.paymentMethod || '',
        medioPago1: labelMedio(v.pagos?.[0]?.medio || v.paymentMethod),
        montoPago1: Number(v.pagos?.[0]?.monto) || 0,
        voucherPago1: v.pagos?.[0]?.voucherNumber || '',
        last4Pago1: v.pagos?.[0]?.last4Digits || '',
        cuotasPago1: v.pagos?.[0]?.cuotas ?? '',
        cantidadPagos: (v.pagos || []).length,
        paidByName: v.paidByName || '',
        anulacionAt: toExcelDate(v.anulacionAt),
        anulacionBy: v.anulacionBy || '',
        anulacionMotivo: v.anulacionMotivo || '',
        cantidadItems: (v.items || []).length,
        metas: Array.from(new Set((v.items || []).map((it) => metaDe(it.tipoPrestacion)))).join(' / '),
      };
    };

    // Columnas: primero las del ítem, luego las de la venta (que se repiten por fila)
    const ITEM_COLS = [
      'nro_item', 'cantidad', 'descripcion', 'tipo', 'tipoPrestacion', 'unidadNegocio', 'meta',
      'precioUnitario', 'descuento', 'subtotal', 'iva', 'total',
      'neto_prorrateado', 'arancelId', 'referenceType', 'referenceId',
      'venta_suma_item_subtotal', 'venta_dif_neto_vs_items',
    ];
    const VENTA_COLS = Object.keys(ventaFields({}));

    const emptyItem = () => ITEM_COLS.reduce((acc, k) => { acc[k] = ''; return acc; }, {});

    // Hoja 1: una fila por venta
    const hojaVentas = ventas.map(ventaFields);

    // Hoja 2: una fila por ítem, con el neto repartido proporcionalmente
    const hojaDetalle = [];
    ventas.forEach((v) => {
      const base = ventaFields(v);
      const items = v.items || [];
      const sumItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
      const netoVenta = Number(v.subtotalNeto) || Number(v.subtotal) || 0;
      const factor = sumItems > 0 ? netoVenta / sumItems : 0;

      if (items.length === 0) {
        hojaDetalle.push({
          ...emptyItem(),
          nro_item: 0,
          descripcion: '(venta sin ítems)',
          venta_suma_item_subtotal: 0,
          venta_dif_neto_vs_items: netoVenta,
          ...base,
        });
        return;
      }
      items.forEach((it, idx) => {
        const itemSubtotal = Number(it.subtotal) || 0;
        hojaDetalle.push({
          nro_item: idx + 1,
          cantidad: Number(it.cantidad) || 0,
          descripcion: it.descripcion || '',
          tipo: it.tipo || '',
          tipoPrestacion: it.tipoPrestacion || '',
          unidadNegocio: it.unidadNegocio || '',
          meta: metaDe(it.tipoPrestacion),
          precioUnitario: Number(it.precioUnitario) || 0,
          descuento: Number(it.descuento) || 0,
          subtotal: itemSubtotal,
          iva: Number(it.iva) || 0,
          total: Number(it.total) || 0,
          // Neto de la venta repartido en proporción al subtotal del ítem
          neto_prorrateado: Math.round(itemSubtotal * factor),
          arancelId: it.arancelId || '',
          referenceType: it.referenceType || '',
          referenceId: it.referenceId || '',
          venta_suma_item_subtotal: sumItems,
          venta_dif_neto_vs_items: netoVenta - sumItems,
          ...base,
        });
      });
    });

    // Hoja 3: resumen por periodo + tipo de prestación
    // casos: se cuentan por `presupuestoNumber` distinto (un presupuesto con varios
    // pagos cuenta como 1 caso). Sin presupuesto, la venta es su propio caso.
    const resumenMap = {};
    const casosPorClave = {};
    ventas
      .filter((v) => (v.estado || 'emitida') !== 'anulada')
      .forEach((v) => {
        const per = toPeriodo(v.createdAt);
        const items = v.items || [];
        const sumItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
        const netoVenta = Number(v.subtotalNeto) || Number(v.subtotal) || 0;
        const factor = sumItems > 0 ? netoVenta / sumItems : 0;
        const casoId = (v.presupuestoNumber || '').trim()
          ? `PRES:${(v.presupuestoNumber || '').trim()}`
          : `VENTA:${v.id}`;
        items.forEach((it) => {
          const tipo = it.tipoPrestacion || '(sin tipo)';
          const key = `${per.periodo}||${tipo}`;
          if (!resumenMap[key]) {
            resumenMap[key] = {
              anio: per.anio, mes: per.mes, nombreMes: per.nombreMes, periodo: per.periodo,
              tipoPrestacion: tipo, meta: metaDe(it.tipoPrestacion),
              items: 0, cantidad: 0, subtotal: 0, neto_prorrateado: 0, total_bruto: 0, casos: 0,
            };
            casosPorClave[key] = new Set();
          }
          const r = resumenMap[key];
          r.items += 1;
          r.cantidad += Number(it.cantidad) || 0;
          r.subtotal += Number(it.subtotal) || 0;
          r.neto_prorrateado += (Number(it.subtotal) || 0) * factor;
          r.total_bruto += Number(it.total) || 0;
          casosPorClave[key].add(casoId);
        });
      });
    Object.keys(resumenMap).forEach((key) => {
      resumenMap[key].casos = casosPorClave[key].size;
    });
    const hojaResumen = Object.values(resumenMap)
      .map((r) => ({ ...r, neto_prorrateado: Math.round(r.neto_prorrateado) }))
      .sort((a, b) => (a.periodo === b.periodo ? b.neto_prorrateado - a.neto_prorrateado : a.periodo.localeCompare(b.periodo)));

    // Totales de control al final del resumen
    const totalNeto = ventas
      .filter((v) => (v.estado || 'emitida') !== 'anulada')
      .reduce((s, v) => s + (Number(v.subtotalNeto) || Number(v.subtotal) || 0), 0);
    hojaResumen.push({
      anio: '', mes: '', nombreMes: '', periodo: '',
      tipoPrestacion: 'TOTAL',
      meta: '',
      items: hojaResumen.reduce((s, r) => s + r.items, 0),
      cantidad: hojaResumen.reduce((s, r) => s + r.cantidad, 0),
      subtotal: hojaResumen.reduce((s, r) => s + r.subtotal, 0),
      neto_prorrateado: hojaResumen.reduce((s, r) => s + r.neto_prorrateado, 0),
      total_bruto: hojaResumen.reduce((s, r) => s + r.total_bruto, 0),
      casos: '',
    });
    hojaResumen.push({
      anio: '', mes: '', nombreMes: '', periodo: '',
      tipoPrestacion: 'NETO LIBRO DE VENTAS (control)',
      meta: '', items: '', cantidad: '', subtotal: '', neto_prorrateado: Math.round(totalNeto), total_bruto: '', casos: '',
    });

    // Hoja 4: resumen agrupado por periodo + meta (casos por presupuestoNumber distinto)
    const metasMap = {};
    const casosPorMeta = {};
    ventas
      .filter((v) => (v.estado || 'emitida') !== 'anulada')
      .forEach((v) => {
        const per = toPeriodo(v.createdAt);
        const items = v.items || [];
        const sumItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
        const netoVenta = Number(v.subtotalNeto) || Number(v.subtotal) || 0;
        const factor = sumItems > 0 ? netoVenta / sumItems : 0;
        const casoId = (v.presupuestoNumber || '').trim()
          ? `PRES:${(v.presupuestoNumber || '').trim()}`
          : `VENTA:${v.id}`;
        items.forEach((it) => {
          const meta = metaDe(it.tipoPrestacion);
          const key = `${per.periodo}||${meta}`;
          if (!metasMap[key]) {
            metasMap[key] = {
              anio: per.anio, mes: per.mes, nombreMes: per.nombreMes, periodo: per.periodo,
              meta, items: 0, cantidad: 0, subtotal: 0, neto_prorrateado: 0, total_bruto: 0, casos: 0,
            };
            casosPorMeta[key] = new Set();
          }
          const m = metasMap[key];
          m.items += 1;
          m.cantidad += Number(it.cantidad) || 0;
          m.subtotal += Number(it.subtotal) || 0;
          m.neto_prorrateado += (Number(it.subtotal) || 0) * factor;
          m.total_bruto += Number(it.total) || 0;
          casosPorMeta[key].add(casoId);
        });
      });
    const hojaMetas = Object.values(metasMap)
      .map((m) => {
        const key = `${m.periodo}||${m.meta}`;
        const casos = casosPorMeta[key].size;
        const neto = Math.round(m.neto_prorrateado);
        return { ...m, casos, neto_prorrateado: neto, ticket_promedio: casos > 0 ? Math.round(neto / casos) : 0 };
      })
      .sort((a, b) => (a.periodo === b.periodo ? b.neto_prorrateado - a.neto_prorrateado : a.periodo.localeCompare(b.periodo)));
    const totalCasos = hojaMetas.reduce((s, r) => s + r.casos, 0);
    const totalNetoMetas = hojaMetas.reduce((s, r) => s + r.neto_prorrateado, 0);
    hojaMetas.push({
      anio: '', mes: '', nombreMes: '', periodo: '',
      meta: 'TOTAL',
      casos: totalCasos,
      items: hojaMetas.reduce((s, r) => s + r.items, 0),
      cantidad: hojaMetas.reduce((s, r) => s + r.cantidad, 0),
      subtotal: hojaMetas.reduce((s, r) => s + r.subtotal, 0),
      neto_prorrateado: totalNetoMetas,
      total_bruto: hojaMetas.reduce((s, r) => s + r.total_bruto, 0),
      ticket_promedio: totalCasos > 0 ? Math.round(totalNetoMetas / totalCasos) : 0,
    });
    hojaMetas.push({
      anio: '', mes: '', nombreMes: '', periodo: '',
      meta: 'NETO LIBRO DE VENTAS (control)',
      casos: '', items: '', cantidad: '', subtotal: '', neto_prorrateado: Math.round(totalNeto), total_bruto: '', ticket_promedio: '',
    });

    const PERIODO_COLS = ['anio', 'mes', 'nombreMes', 'periodo'];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(hojaVentas, { header: VENTA_COLS }),
      'Ventas'
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(hojaDetalle, { header: [...ITEM_COLS, ...VENTA_COLS] }),
      'Detalle Prestaciones'
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(hojaResumen, {
        header: [...PERIODO_COLS, 'tipoPrestacion', 'meta', 'casos', 'items', 'cantidad', 'subtotal', 'neto_prorrateado', 'total_bruto'],
      }),
      'Resumen x Prestación'
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(hojaMetas, {
        header: [...PERIODO_COLS, 'meta', 'casos', 'items', 'cantidad', 'subtotal', 'neto_prorrateado', 'ticket_promedio', 'total_bruto'],
      }),
      'Resumen x Meta'
    );
    XLSX.writeFile(wb, `libro_ventas_detalle_${desde.replace(/-/g, '')}_${hasta.replace(/-/g, '')}.xlsx`);
  };

  return (
    <Box sx={{ pb: 6 }}>
      <Typography variant="h4" fontWeight={800} color={PRIMARY} gutterBottom>Libro de Ventas</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>Registro de ventas del período (formato SII Chile).</Typography>

      {/* Filtros */}
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, my: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField size="small" fullWidth type="date" label="Desde" value={desde}
              onChange={(e) => setDesde(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField size="small" fullWidth type="date" label="Hasta" value={hasta}
              onChange={(e) => setHasta(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField select size="small" fullWidth label="Sucursal" value={branchId}
              onChange={(e) => setBranchId(e.target.value)} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">Todas</MenuItem>
              {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField select size="small" fullWidth label="Documento" value={tipoDoc}
              onChange={(e) => setTipoDoc(e.target.value)} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="boleta">Boleta</MenuItem>
              <MenuItem value="factura">Factura</MenuItem>
              <MenuItem value="exento">Exento</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <TextField select size="small" fullWidth label="Estado" value={estado}
              onChange={(e) => setEstado(e.target.value)} InputLabelProps={{ shrink: true }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="emitida">Emitida</MenuItem>
              <MenuItem value="anulada">Anulada</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 1 }}>
            <Button fullWidth variant="contained" startIcon={<FilterAltIcon />} onClick={aplicar} sx={{ bgcolor: PRIMARY }}>
              Aplicar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {/* Tabla */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>Documentos ({ventas.length})</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<DownloadIcon />} onClick={exportarCSV} disabled={!ventas.length}>Exportar CSV</Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TableViewIcon />}
                  onClick={exportarExcel}
                  disabled={!ventas.length}
                >
                  Excel con detalle
                </Button>
              </Stack>
            </Stack>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: 'grey.50' } }}>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Folio</TableCell>
                    <TableCell>RUT</TableCell>
                    <TableCell>Razón Social</TableCell>
                    <TableCell>Medio de Pago</TableCell>
                    <TableCell>Cód. Autorización</TableCell>
                    <TableCell>Últ. 4 Dígitos</TableCell>
                    <TableCell align="right">Neto</TableCell>
                    <TableCell align="right">IVA</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <TableRow key={i}><TableCell colSpan={13}><Skeleton height={26} /></TableCell></TableRow>
                    ))
                  ) : ventas.length === 0 ? (
                    <TableRow><TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {buscado ? 'Sin resultados para el período seleccionado.' : 'Aplica un filtro para ver resultados.'}
                    </TableCell></TableRow>
                  ) : ventas.map((v) => (
                    <TableRow key={v.id} hover sx={{ opacity: (v.estado || 'emitida') === 'anulada' ? 0.5 : 1 }}>
                      <TableCell>{branchesMap[v.branchId]?.name || '–'}</TableCell>
                      <TableCell>{fmtFecha(v.createdAt)}</TableCell>
                      <TableCell>{v.tipoDocumento || 'boleta'}</TableCell>
                      <TableCell>{v.folioDocumento || v.boletaNumber || '–'}</TableCell>
                      <TableCell>{v.rutEmpresa || v.ownerRut || '–'}</TableCell>
                      <TableCell>{v.razonSocial || v.ownerName || '–'}</TableCell>
                      <TableCell>{labelMedio(v.pagos?.[0]?.medio || v.paymentMethod)}</TableCell>
                      <TableCell>{v.pagos?.[0]?.voucherNumber || '–'}</TableCell>
                      <TableCell>{v.pagos?.[0]?.last4Digits || '–'}</TableCell>
                      <TableCell align="right">{CLP(v.subtotalNeto || v.subtotal || 0)}</TableCell>
                      <TableCell align="right">{CLP(v.montoIVA || v.iva || 0)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(v.totalFinal || v.total || 0)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={v.estado || 'emitida'}
                          color={(v.estado || 'emitida') === 'anulada' ? 'default' : 'success'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Totales */}
            <Divider />
            <Grid container sx={{ p: 2 }} spacing={1}>
              <TotalChip label="Neto" value={CLP(totales.neto)} />
              <TotalChip label="IVA" value={CLP(totales.iva)} />
              <TotalChip label="Total" value={CLP(totales.total)} bold />
            </Grid>
            <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
              <Chip size="small" label={`${totales.boletas} boletas`} />
              <Chip size="small" label={`${totales.facturas} facturas`} />
              <Chip size="small" color="warning" label={`${totales.anuladas} anuladas`} />
            </Stack>
          </Paper>
        </Grid>

        {/* Donut por unidad de negocio */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Ventas por unidad de negocio</Typography>
            {porUnidad.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sin datos.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={porUnidad} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {porUnidad.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip formatter={(val) => CLP(val)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function TotalChip({ label, value, bold }) {
  return (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant={bold ? 'h6' : 'body1'} fontWeight={bold ? 800 : 600} color={bold ? PRIMARY : 'text.primary'}>{value}</Typography>
      </Box>
    </Grid>
  );
}
